// Single-outlet financial health dashboard.
//
// All figures are net of GST. Receivables/payables are always as of today;
// the KPI cards and chart follow the selected month-aligned period.

import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { api } from '../lib/api.js';
import type {
  AgingBucket,
  DashboardAgingDetail,
  DashboardResult,
  DashboardStockDetail,
  StockControlKind,
} from '../lib/api.js';
import {
  DEFAULT_PERIOD,
  PERIOD_OPTIONS,
  fillMonths,
  formatDelta,
  formatMoney,
  formatPct,
  loadOutletDashboard,
  previousTotals,
  resolvePeriod,
  scoreHealth,
  sumMonths,
  type DeltaValue,
  type PeriodPreset,
  type PeriodWindow,
} from '../lib/finance.js';
import { FINANCE_CHART_STYLES, renderFinanceChart } from './finance-chart.js';
import './finance-stock.js';
import { TAB_UI_STYLES } from '../styles/tab-ui.js';

interface DrillState {
  mode: 'aging' | 'stock';
  title: string;
  loading: boolean;
  error: string;
  aging?: DashboardAgingDetail;
  stock?: DashboardStockDetail;
}

function fmtDate(ms: number): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

@customElement('finance-dashboard')
export class FinanceDashboard extends LitElement {
  static styles = [TAB_UI_STYLES, FINANCE_CHART_STYLES, css`
    :host { display: flex; flex-direction: column; height: 100%; min-height: 0; overflow: hidden; }
    .page-scroll { flex: 1; overflow-y: auto; min-height: 0; padding: 0 1rem 1.25rem; }
    .head { display: flex; flex-wrap: wrap; align-items: flex-end; justify-content: space-between; gap: 0.75rem; margin-bottom: 0.75rem; }
    .head h2 { margin: 0 0 0.15rem; font-size: 1.1rem; }
    .sub { margin: 0; font-size: 0.76rem; color: var(--color-text-secondary); }
    .chips { display: flex; flex-wrap: wrap; gap: 0.35rem; }
    .chip { border: 1px solid var(--color-border); background: var(--color-surface); border-radius: 999px; padding: 6px 12px; font-family: inherit; font-size: 0.76rem; font-weight: 600; cursor: pointer; color: var(--color-text-secondary); }
    .chip:hover { border-color: var(--color-primary); color: var(--color-primary); }
    .chip.active { background: var(--color-primary); border-color: var(--color-primary); color: #fff; }

    .state { text-align: center; padding: 2.5rem 1rem; color: var(--color-text-secondary); }
    .error { margin-bottom: 0.75rem; padding: 0.65rem 0.85rem; border-radius: 10px; background: #fdecea; color: #b71c1c; font-size: 0.84rem; }

    .kpi-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 0.65rem; margin-bottom: 0.85rem; }
    .kpi { border: 1px solid var(--color-border); border-radius: 14px; background: var(--color-surface); padding: 0.8rem 0.9rem; min-width: 0; }
    .kpi .label { font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; color: var(--color-text-secondary); }
    .kpi .value { font-size: 1.25rem; font-weight: 800; margin-top: 0.25rem; overflow-wrap: anywhere; }
    .kpi .value.neg { color: var(--color-error, #d93025); }
    .kpi .sub-line { margin-top: 0.2rem; font-size: 0.72rem; color: var(--color-text-secondary); }
    .delta { display: inline-flex; align-items: center; gap: 0.15rem; margin-left: 0.35rem; font-size: 0.68rem; font-weight: 700; }
    .delta.good { color: var(--color-success, #188038); }
    .delta.bad { color: var(--color-error, #d93025); }
    .delta.flat { color: var(--color-text-secondary); }

    .panel { border: 1px solid var(--color-border); border-radius: 14px; background: var(--color-surface); padding: 0.9rem; margin-bottom: 0.85rem; }
    .panel h3 { margin: 0 0 0.6rem; font-size: 0.9rem; }

    .health { display: flex; gap: 1rem; align-items: center; }
    .ring { position: relative; width: 92px; height: 92px; flex-shrink: 0; }
    .ring svg { width: 92px; height: 92px; transform: rotate(-90deg); }
    .ring .bg { fill: none; stroke: #eef1f5; stroke-width: 9; }
    .ring .fg { fill: none; stroke-width: 9; stroke-linecap: round; transition: stroke-dashoffset 0.35s ease; }
    .health.healthy .ring .fg { stroke: var(--color-success, #188038); }
    .health.watch .ring .fg { stroke: var(--color-warning, #e8a317); }
    .health.risk .ring .fg { stroke: var(--color-error, #d93025); }
    .health.no-data .ring .fg { stroke: var(--color-border); }
    .ring .score { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; flex-direction: column; font-weight: 800; font-size: 1.35rem; }
    .ring .score small { font-size: 0.58rem; font-weight: 600; color: var(--color-text-secondary); }
    .health-copy h4 { margin: 0 0 0.2rem; font-size: 1rem; }
    .health-copy .band { display: inline-block; border-radius: 999px; padding: 2px 9px; font-size: 0.68rem; font-weight: 800; margin-bottom: 0.35rem; }
    .health.healthy .band { background: #e6f4ea; color: #137333; }
    .health.watch .band { background: #fff4d6; color: #8a5a00; }
    .health.risk .band { background: #fce8e6; color: #a50e0e; }
    .health.no-data .band { background: #eef1f5; color: var(--color-text-secondary); }
    .health-copy ul { margin: 0.15rem 0 0; padding-left: 1.05rem; font-size: 0.76rem; color: var(--color-text-secondary); }
    .health-copy li { margin-bottom: 0.15rem; }

    .aging { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 0.65rem; }
    .aging-card { border: 1px solid var(--color-border); border-radius: 12px; padding: 0.7rem 0.8rem; }
    .aging-card .top { display: flex; justify-content: space-between; align-items: baseline; gap: 0.5rem; margin-bottom: 0.45rem; }
    .aging-card .top strong { font-size: 0.82rem; }
    .aging-card .top span { font-size: 0.9rem; font-weight: 800; }
    .bucket {
      display: grid;
      grid-template-columns: 58px 1fr 78px 12px;
      align-items: center;
      gap: 0.45rem;
      width: 100%;
      font: inherit;
      font-size: 0.72rem;
      margin: 0.1rem 0;
      padding: 0.25rem 0.2rem;
      background: none;
      border: none;
      border-radius: 7px;
      color: inherit;
      text-align: left;
      cursor: pointer;
    }
    .bucket:hover { background: #f2f6fd; }
    .bucket:focus-visible { outline: 2px solid var(--color-primary); outline-offset: 1px; }
    .bucket .bar { height: 7px; border-radius: 999px; background: #eef1f5; overflow: hidden; }
    .bucket .bar i { display: block; height: 100%; border-radius: 999px; background: var(--color-primary); }
    .bucket .bar i.overdue { background: var(--color-warning, #e8a317); }
    .bucket .amt { text-align: right; font-variant-numeric: tabular-nums; }
    .bucket .chev { color: var(--color-text-secondary); font-weight: 800; }

    .kpi.clickable { cursor: pointer; transition: border-color 0.15s, box-shadow 0.15s; }
    .kpi.clickable:hover { border-color: var(--color-primary); box-shadow: 0 2px 8px rgba(15,35,80,0.08); }

    /* ── drill-down modal ── */
    .modal-overlay {
      position: fixed;
      inset: 0;
      z-index: 300;
      background: rgba(15,23,42,0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 1rem;
    }
    .modal {
      width: 100%;
      max-width: 920px;
      max-height: 86dvh;
      display: flex;
      flex-direction: column;
      background: var(--color-surface);
      border-radius: 16px;
      overflow: hidden;
      box-shadow: 0 24px 60px rgba(15,35,80,0.3);
    }
    .modal-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.75rem;
      padding: 0.9rem 1.1rem;
      border-bottom: 1px solid var(--color-border);
      flex-shrink: 0;
    }
    .modal-header h3 { margin: 0; font-size: 0.98rem; }
    .modal-close {
      width: 36px;
      height: 36px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: none;
      border-radius: 10px;
      background: none;
      color: var(--color-text-secondary);
      font-size: 1.3rem;
      line-height: 1;
      cursor: pointer;
    }
    .modal-close:hover { background: #f2f6fd; color: var(--color-text); }
    .modal-body { padding: 1rem 1.1rem 1.2rem; overflow-y: auto; min-height: 0; }
    .drill-summary {
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;
      gap: 0.5rem;
      margin-bottom: 0.8rem;
      font-size: 0.8rem;
      color: var(--color-text-secondary);
    }
    .drill-summary strong { font-size: 1.05rem; color: var(--color-text); }
    .modal-body h4 { margin: 0.9rem 0 0.45rem; font-size: 0.82rem; }
    .drill-table-wrap {
      overflow: auto;
      border: 1px solid var(--color-border);
      border-radius: 12px;
      background: var(--color-surface);
    }
    .drill-table { width: 100%; border-collapse: collapse; font-size: 0.76rem; white-space: nowrap; }
    .drill-table th, .drill-table td { padding: 8px 10px; border-bottom: 1px solid var(--color-border); text-align: left; }
    .drill-table th { position: sticky; top: 0; background: var(--color-surface); color: var(--color-text-secondary); font-size: 0.66rem; text-transform: uppercase; letter-spacing: 0.03em; z-index: 1; }
    .drill-table th.num, .drill-table td.num { text-align: right; font-variant-numeric: tabular-nums; }
    .drill-table tbody tr:hover { background: #f7f9fd; }
    .drill-table .drill-code { display: block; font-size: 0.66rem; color: var(--color-text-secondary); font-weight: 600; }

    @media (max-width: 720px) {
      .health { align-items: flex-start; }
      .head { align-items: flex-start; }
      .modal-overlay { align-items: flex-end; padding: 0; }
      .modal { max-width: none; max-height: 92dvh; border-radius: 18px 18px 0 0; }
      .modal-body { padding-bottom: calc(1rem + env(safe-area-inset-bottom)); }
    }
  `];

  @property({ type: String }) storeCode = '';

  @state() private _preset: PeriodPreset = DEFAULT_PERIOD;
  @state() private _window: PeriodWindow = resolvePeriod(DEFAULT_PERIOD);
  @state() private _data: DashboardResult | null = null;
  @state() private _loading = true;
  @state() private _error = '';
  @state() private _drill: DrillState | null = null;
  private _seq = 0;
  private _drillSeq = 0;

  protected updated(changed: Map<PropertyKey, unknown>) {
    if (changed.has('storeCode')) {
      this._preset = DEFAULT_PERIOD;
      this._window = resolvePeriod(DEFAULT_PERIOD);
      this._data = null;
      this._closeDrill();
      void this._load();
    }
  }

  private _setPreset(preset: PeriodPreset) {
    if (this._preset === preset) return;
    this._preset = preset;
    this._window = resolvePeriod(preset);
    this._data = null;
    void this._load();
  }

  private async _load() {
    if (!this.storeCode) {
      this._loading = false;
      return;
    }
    const seq = ++this._seq;
    this._loading = true;
    this._error = '';
    try {
      const data = await loadOutletDashboard(this.storeCode, this._window);
      if (seq !== this._seq) return;
      this._data = data;
    } catch (e: any) {
      if (seq !== this._seq) return;
      this._data = null;
      this._error = e?.message || 'Failed to load the financial dashboard';
    } finally {
      if (seq === this._seq) this._loading = false;
    }
  }

  private _openAgingDrill(kind: 'ar' | 'ap', bucket: AgingBucket, label: string) {
    const seq = ++this._drillSeq;
    this._drill = {
      mode: 'aging',
      title: `${kind === 'ar' ? 'Customer receivables' : 'Supplier payables'} · ${label}`,
      loading: true,
      error: '',
    };
    api.accountingDashboardAging(this.storeCode, { kind, bucket, asOf: Date.now() })
      .then((res) => {
        if (seq !== this._drillSeq || !this._drill) return;
        this._drill = { ...this._drill, loading: false, aging: res.data };
      })
      .catch((e: any) => {
        if (seq !== this._drillSeq || !this._drill) return;
        this._drill = { ...this._drill, loading: false, error: e?.message || 'Failed to load ageing detail' };
      });
  }

  private _openStockDrill(kind: StockControlKind, label: string) {
    const seq = ++this._drillSeq;
    this._drill = { mode: 'stock', title: label, loading: true, error: '' };
    api.accountingDashboardStock(this.storeCode, { kind, asOf: Date.now() })
      .then((res) => {
        if (seq !== this._drillSeq || !this._drill) return;
        this._drill = { ...this._drill, loading: false, stock: res.data };
      })
      .catch((e: any) => {
        if (seq !== this._drillSeq || !this._drill) return;
        this._drill = { ...this._drill, loading: false, error: e?.message || 'Failed to load stock detail' };
      });
  }

  private _closeDrill() {
    this._drillSeq++;
    this._drill = null;
  }

  private _deltaBadge(delta: DeltaValue | null, invert = false) {
    if (!delta) return nothing;
    const good = invert ? delta.direction === 'down' : delta.direction === 'up';
    const cls = delta.direction === 'flat' ? 'flat' : good ? 'good' : 'bad';
    const arrow = delta.direction === 'up' ? '▲' : delta.direction === 'down' ? '▼' : '';
    return html`<span class="delta ${cls}">${arrow} ${delta.text}</span>`;
  }

  private _renderKpis(data: DashboardResult) {
    const totals = sumMonths(data.months, this._window.from, this._window.to);
    const prev = previousTotals(data.months, this._window);
    const grossMargin = totals.sales > 0 ? totals.grossProfit / totals.sales : 0;
    const netMargin = totals.sales > 0 ? totals.netProfit / totals.sales : 0;
    const costRatio = totals.sales > 0 ? (totals.expenses + totals.fees) / totals.sales : 0;
    const arOverdue = Math.max(0, data.ar.d31_60 + data.ar.d61_90 + data.ar.d90p);
    const arOverdueRatio = data.ar.total > 0 ? arOverdue / data.ar.total : 0;

    return html`
      <div class="kpi-grid">
        <div class="kpi">
          <div class="label">Revenue (net)</div>
          <div class="value">${formatMoney(totals.sales)}${this._deltaBadge(formatDelta(totals.sales, prev?.sales))}</div>
          <div class="sub-line">Net of GST</div>
        </div>
        <div class="kpi">
          <div class="label">Gross profit</div>
          <div class="value">${formatMoney(totals.grossProfit)}</div>
          <div class="sub-line">Gross margin ${formatPct(grossMargin)}</div>
        </div>
        <div class="kpi">
          <div class="label">Expenses + fees</div>
          <div class="value">${formatMoney(totals.expenses + totals.fees)}${this._deltaBadge(formatDelta(totals.expenses + totals.fees, prev ? prev.expenses + prev.fees : null), true)}</div>
          <div class="sub-line">${formatPct(costRatio)} of revenue</div>
        </div>
        <div class="kpi">
          <div class="label">Net profit</div>
          <div class="value ${totals.netProfit < 0 ? 'neg' : ''}">${formatMoney(totals.netProfit)}${this._deltaBadge(formatDelta(totals.netProfit, prev?.netProfit))}</div>
          <div class="sub-line">Net margin ${formatPct(netMargin)}</div>
        </div>
        <div class="kpi clickable" role="button" tabindex="0"
          title="Show all outstanding customer invoices"
          @click=${() => this._openAgingDrill('ar', 'all', 'All outstanding')}
          @keydown=${(e: KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              this._openAgingDrill('ar', 'all', 'All outstanding');
            }
          }}>
          <div class="label">Receivables</div>
          <div class="value">${formatMoney(data.ar.total)}</div>
          <div class="sub-line">${formatPct(arOverdueRatio)} over 30 days · ${data.ar.parties} parties · details ›</div>
        </div>
        <div class="kpi clickable" role="button" tabindex="0"
          title="Show all outstanding supplier bills"
          @click=${() => this._openAgingDrill('ap', 'all', 'All outstanding')}
          @keydown=${(e: KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              this._openAgingDrill('ap', 'all', 'All outstanding');
            }
          }}>
          <div class="label">Payables</div>
          <div class="value">${formatMoney(data.ap.total)}</div>
          <div class="sub-line">As of today · ${data.ap.parties} suppliers · details ›</div>
        </div>
      </div>
    `;
  }

  private _renderHealth(data: DashboardResult) {
    const totals = sumMonths(data.months, this._window.from, this._window.to);
    const prev = previousTotals(data.months, this._window);
    const health = scoreHealth(totals, data.ar, data.ap, prev);
    const radius = 34;
    const circumference = 2 * Math.PI * radius;
    const progress = health.score == null ? 0 : Math.max(0, Math.min(100, health.score));
    const offset = circumference - (progress / 100) * circumference;
    return html`
      <div class="panel health ${health.band}" data-testid="finance-health">
        <div class="ring">
          <svg viewBox="0 0 92 92" aria-hidden="true">
            <circle class="bg" cx="46" cy="46" r=${radius}></circle>
            <circle class="fg" cx="46" cy="46" r=${radius}
              stroke-dasharray=${circumference.toFixed(1)}
              stroke-dashoffset=${offset.toFixed(1)}></circle>
          </svg>
          <div class="score">
            ${health.score == null ? '—' : health.score}
            <small>/ 100</small>
          </div>
        </div>
        <div class="health-copy">
          <span class="band">${health.label}</span>
          <h4>Financial health</h4>
          <ul>${health.reasons.map((r) => html`<li>${r}</li>`)}</ul>
        </div>
      </div>
    `;
  }

  private _renderAging(data: DashboardResult) {
    const card = (title: string, summary: DashboardResult['ar'], kind: 'ar' | 'ap') => {
      const total = summary.total;
      const pct = (v: number) => (total > 0 ? (v / total) * 100 : 0);
      const buckets: Array<{ key: AgingBucket; label: string; value: number; overdue: boolean }> = [
        { key: 'current', label: 'Current', value: summary.current, overdue: false },
        { key: 'd1_30', label: '1–30', value: summary.d1_30, overdue: true },
        { key: 'd31_60', label: '31–60', value: summary.d31_60, overdue: true },
        { key: 'd61_90', label: '61–90', value: summary.d61_90, overdue: true },
        { key: 'd90p', label: '90+', value: summary.d90p, overdue: true },
      ];
      return html`
        <div class="aging-card">
          <div class="top"><strong>${title}</strong><span>${formatMoney(total)}</span></div>
          ${buckets.map((b) => html`
            <button class="bucket" data-testid="finance-aging-${kind}-${b.key}"
              title=${`Show ${title.toLowerCase()} · ${b.label}`}
              @click=${() => this._openAgingDrill(kind, b.key, b.label)}>
              <span>${b.label}</span>
              <span class="bar"><i class=${b.overdue ? 'overdue' : ''} style="width:${pct(b.value).toFixed(1)}%"></i></span>
              <span class="amt">${formatMoney(b.value)}</span>
              <span class="chev">›</span>
            </button>
          `)}
        </div>
      `;
    };
    return html`
      <div class="panel">
        <h3>Ageing — as of today · click a bucket for detail</h3>
        <div class="aging">
          ${card('Customer receivables', data.ar, 'ar')}
          ${card('Supplier payables', data.ap, 'ap')}
        </div>
      </div>
    `;
  }

  private _renderAgingDetail(a: DashboardAgingDetail) {
    const bucketLabel = a.bucket === 'all' ? 'All outstanding' : a.bucket;
    return html`
      <div class="drill-summary">
        <strong>${formatMoney(a.total)}</strong>
        <span>${bucketLabel} · ${a.parties.length} party(ies) · ${a.invoices.length} invoice(s)</span>
      </div>
      ${a.parties.length ? html`
        <h4>Parties making up this bucket</h4>
        <div class="drill-table-wrap">
          <table class="drill-table">
            <thead><tr>
              <th>Party</th><th class="num">In bucket</th>
              <th class="num">Total outstanding</th><th class="num">Invoices</th>
            </tr></thead>
            <tbody>
              ${a.parties.map((p) => html`
                <tr>
                  <td>${p.partyName || `#${p.partyId}`}</td>
                  <td class="num">${formatMoney(p.amount)}</td>
                  <td class="num">${formatMoney(p.total)}</td>
                  <td class="num">${p.count}</td>
                </tr>
              `)}
            </tbody>
          </table>
        </div>
      ` : html`<div class="state">No outstanding ${a.kind === 'ar' ? 'receivables' : 'payables'} in this bucket.</div>`}
      ${a.invoices.length ? html`
        <h4>Invoice detail</h4>
        <div class="drill-table-wrap">
          <table class="drill-table">
            <thead><tr>
              <th>Party</th><th>Ref</th><th>Invoice date</th><th>Due date</th>
              <th class="num">Days overdue</th><th class="num">Outstanding</th>
            </tr></thead>
            <tbody>
              ${a.invoices.map((i) => html`
                <tr>
                  <td>${i.partyName || `#${i.partyId}`}</td>
                  <td>${i.refNo || i.id}</td>
                  <td>${fmtDate(i.date)}</td>
                  <td>${fmtDate(i.dueDate)}</td>
                  <td class="num">${i.daysOverdue > 0 ? `${Math.round(i.daysOverdue)}d` : '—'}</td>
                  <td class="num">${formatMoney(i.outstanding)}</td>
                </tr>
              `)}
            </tbody>
          </table>
        </div>
      ` : nothing}
    `;
  }

  private _renderStockDetail(s: DashboardStockDetail) {
    const labels: Record<StockControlKind, string> = {
      value: 'Items making up stock value',
      low: 'Low stock items',
      out: 'Out of stock items',
      negative: 'Negative QOH items',
      slow: 'Slow movers (no sale in 90 days)',
    };
    return html`
      <div class="drill-summary">
        <strong>${s.items.length}${s.total > s.items.length ? ` of ${s.total}` : ''} item(s)</strong>
        <span>${labels[s.kind]} · as of ${fmtDate(s.asOf)}</span>
      </div>
      ${s.items.length ? html`
        <div class="drill-table-wrap">
          <table class="drill-table">
            <thead><tr>
              <th>Item</th><th>Type</th><th class="num">QOH</th><th class="num">Reorder</th>
              <th class="num">Unit cost</th><th class="num">Stock value</th>
              <th>Last sale</th><th>Last receive</th>
            </tr></thead>
            <tbody>
              ${s.items.map((it) => html`
                <tr>
                  <td>
                    <strong>${it.name || it.code}</strong>
                    ${it.code ? html`<span class="drill-code">${it.code}</span>` : nothing}
                  </td>
                  <td>${it.typeName || '—'}</td>
                  <td class="num">${(Number(it.qoh) || 0).toFixed(2)}</td>
                  <td class="num">${(Number(it.reorderLevel) || 0).toFixed(2)}</td>
                  <td class="num">${formatMoney(it.unitCost)}</td>
                  <td class="num">${formatMoney(it.stockValue)}</td>
                  <td>${it.latestSalesDate ? fmtDate(it.latestSalesDate) : 'Never'}</td>
                  <td>${it.latestReceiveDate ? fmtDate(it.latestReceiveDate) : '—'}</td>
                </tr>
              `)}
            </tbody>
          </table>
        </div>
      ` : html`<div class="state">No items in this group.</div>`}
    `;
  }

  private _renderDrilldown() {
    const d = this._drill;
    if (!d) return nothing;
    return html`
      <div class="modal-overlay" data-testid="finance-drilldown"
        @click=${(e: Event) => { if (e.target === e.currentTarget) this._closeDrill(); }}>
        <div class="modal" role="dialog" aria-modal="true" aria-label=${d.title}>
          <div class="modal-header">
            <h3>${d.title}</h3>
            <button class="modal-close" aria-label="Close" @click=${() => this._closeDrill()}>&times;</button>
          </div>
          <div class="modal-body">
            ${d.loading ? html`<div class="state">Loading detail…</div>` : nothing}
            ${d.error ? html`<div class="error">⚠ ${d.error}</div>` : nothing}
            ${!d.loading && !d.error && d.aging ? this._renderAgingDetail(d.aging) : nothing}
            ${!d.loading && !d.error && d.stock ? this._renderStockDetail(d.stock) : nothing}
          </div>
        </div>
      </div>
    `;
  }

  render() {
    const data = this._data;
    return html`
      <div class="page-scroll">
        <div class="head">
          <div>
            <h2>Financial health</h2>
            <p class="sub">Net of GST · Receivables/payables as of today</p>
          </div>
          <div class="chips">
            ${PERIOD_OPTIONS.map((p) => html`
              <button class="chip ${this._preset === p.id ? 'active' : ''}"
                data-testid="finance-period-${p.id}"
                @click=${() => this._setPreset(p.id)}>${p.label}</button>
            `)}
          </div>
        </div>

        ${this._error ? html`<div class="error" data-testid="finance-error">⚠ ${this._error}</div>` : nothing}
        ${this._loading && !data ? html`<div class="state">Loading financial data…</div>` : nothing}
        ${!this._loading && !data && !this._error ? html`<div class="state">No financial data.</div>` : nothing}
        ${data ? html`
          ${this._renderHealth(data)}
          ${this._renderKpis(data)}
          ${data.stock ? html`
            <stock-control-panel .stock=${data.stock} .drilldown=${true}
              @stock-drilldown=${(e: CustomEvent<{ kind: StockControlKind; label: string }>) =>
                this._openStockDrill(e.detail.kind, e.detail.label)}
            ></stock-control-panel>
          ` : nothing}
          <div class="panel">
            <h3>Last 6 months</h3>
            ${renderFinanceChart(fillMonths(data.months, this._window.chartFrom, this._window.to))}
          </div>
          ${this._renderAging(data)}
        ` : nothing}
      </div>
      ${this._renderDrilldown()}
    `;
  }
}
